import React, { type ReactNode, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { cssMap } from '@atlaskit/css';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import FieldIcon from '@atlaskit/icon/core/field';
import { Box } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

export const messages: {
	actionCompleted: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	actionFailed: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	defaultFieldIconLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	actionCompleted: {
		id: 'conversation-assistant-message-actions.jira-update-work-item-all-fields-action-renderer.action-completed.non-final',
		defaultMessage: 'Action completed',
		description: 'Title for the action completed icon',
	},
	actionFailed: {
		id: 'conversation-assistant-message-actions.jira-update-work-item-all-fields-action-renderer.action-failed.non-final',
		defaultMessage: 'Action failed',
		description: 'Title for the action failed icon',
	},
	defaultFieldIconLabel: {
		id: 'conversation-assistant-message-actions.jira-update-work-item-all-fields-action-renderer.generic-field-icon-label.non-final',
		defaultMessage: 'Field icon',
		description: 'Default label for generic fields',
	},
});

const styles = cssMap({
	root: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	iconHidden: {
		opacity: 0,
		width: '24px',
		height: '24px',
	},
});

type Props = {
	icon: ReactNode;
	showIcon: boolean;
	isCompleted: boolean;
	isLoading: boolean;
	isFailed: boolean;
	isBulkActioning?: boolean;
};

export const StatusIcon = ({
	showIcon,
	icon,
	isCompleted,
	isLoading,
	isFailed,
	isBulkActioning = false,
}: Props): React.JSX.Element => {
	di(FieldIcon);

	const { formatMessage } = useIntl();

	const displayIcon = useMemo(() => {
		if (isLoading) {
			return (
				<Skeleton
					testId="update-work-item-all-fields-action-renderer.ui.card-fields-list.status-icon.loading"
					width="24px"
					height="24px"
					borderRadius={token('radius.full')}
					isShimmering
				/>
			);
		}

		if (isFailed) {
			return (
				<CrossIcon
					label={formatMessage(messages.actionFailed)}
					color={token('color.icon.danger')}
					size="small"
				/>
			);
		}

		if (isCompleted) {
			return (
				<CheckMarkIcon
					label={formatMessage(messages.actionCompleted)}
					color={token('color.icon.success')}
					size="small"
				/>
			);
		}

		// We don't want to override the fail/completed status icons if we are bulk dismissing an issue, andh
		// hence we render the skeleton after the checks if so.
		if (isBulkActioning) {
			return (
				<Skeleton
					testId="update-work-item-all-fields-action-renderer.ui.card-fields-list.status-icon.loading"
					width="24px"
					height="24px"
					borderRadius={token('radius.full')}
					isShimmering
				/>
			);
		}

		if (!icon) {
			return (
				<FieldIcon
					label={formatMessage(messages.defaultFieldIconLabel)}
					size="small"
					color={token('color.icon.subtle')}
				/>
			);
		}

		// Display the icon corresponding to the field type
		return icon;
	}, [icon, isCompleted, isLoading, isFailed, formatMessage, isBulkActioning]);

	const shouldDisplayIcon = showIcon || isCompleted || isLoading || isFailed || isBulkActioning;

	return (
		<Box
			testId="update-work-item-all-fields-action-renderer.ui.card-fields-list.status-icon.container"
			xcss={shouldDisplayIcon ? styles.root : styles.iconHidden}
		>
			{displayIcon}
		</Box>
	);
};
